/**
 * DealDashboard — the deal page as a dashboard, not a form.
 *
 * Designed to read top-down: the numbers that decide the deal, then the
 * findings that explain them, then the evidence they rest on. The four-way
 * rent comparison is the headline because it is the question a deal page
 * exists to answer — what the broker said, where we underwrote, what the rent
 * roll shows signed, and what has actually signed nearby.
 *
 * Every figure traces to a source file, sheet and row through the comp
 * database. Nothing is computed from an assumption that is not itself
 * recorded, and a panel with no data says "not held" rather than showing a
 * zero — a deal with no OpEx and a deal with $0 of OpEx are different facts.
 */

import { useEffect, useMemo, useState } from "react";
import {
  loadMarketRent, loadOpexLines, loadDataQuality, loadRentRoll,
  loadNearbyComps, loadHandpickedComps, addHandpickedComp,
  removeHandpickedComp, loadAssignedComps, buildOpexMatrix,
  buildFourWayRent, buildMarkToMarket, buildExpirySchedule,
} from "../../supabase";
import { suggestComps, impliedMarketRent } from "../../services/compSuggest";
import { markToMarket } from "../../services/dealAnalytics";
import { fmt } from "../../utils/formatters";
import * as B from "../common/brand";
import { GableHeadline } from "../common/PPGable";
import DealCharts from "./DealCharts";

/* ── shared vocabulary ────────────────────────────────────────────── */
// Expense evidence is thin enough that a 2-mile radius usually returns nothing.
const OPEX_RADIUS_MILES = 5;
// Rounded before formatting: toLocaleString on a raw float renders
// "$214,060.137", which reads as a typo rather than as an annual figure.
const money = (v) => (Number.isFinite(v) && v !== 0 ? fmt.currencyOrDash(Math.round(v)) : "—");
const psf = (v) => (Number.isFinite(v) && v > 0 ? `$${Number(v).toFixed(2)}` : "—");
const sf0 = (v) => (Number.isFinite(Number(v)) && Number(v) ? Number(v).toLocaleString() : "—");
const pct1 = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : "—");

const CARD = B.CARD;
const HEAD = B.CARD_HEAD;
const H = B.TITLE;
const SUB = B.SUBTITLE;
const TH = B.TH;
const TD = B.TD;

function Card({ title, sub, right, children, className = "" }) {
  return (
    <section className={`${CARD} ${className}`}>
      {(title || right) && (
        <header className={HEAD}>
          <div className="min-w-0">
            <h3 className={H}>{title}</h3>
            {sub && <p className={`${SUB} mt-0.5`}>{sub}</p>}
          </div>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <div className="px-5 py-8 text-[12.5px] text-[#8a8a8a] leading-relaxed">{children}</div>;
}

/* ── KPI strip ────────────────────────────────────────────────────── */
function Kpi({ label, value, sub, tone = "slate", hint }) {
  // Red is the brand's accent and is spent carefully: a KPI only turns red
  // when the point of the number is that it is a problem.
  const tones = {
    slate: "text-[#f4f4f4]", emerald: "text-[#4f9d76]",
    amber: "text-[#c8922f]", rose: "text-[#e93529]", sky: "text-[#f4f4f4]",
  };
  return (
    <div className="px-5 py-4 flex-1 min-w-[150px]" title={hint || ""}>
      <div className={B.LABEL}>{label}</div>
      <div className={`mt-1.5 text-[26px] leading-none font-light tracking-tight tabular-nums ${tones[tone]}`}>
        {value}
      </div>
      {sub && <div className="mt-2 text-[11px] text-[#8a8a8a] leading-snug">{sub}</div>}
    </div>
  );
}

/* ── the insight strip: findings in plain language ────────────────── */
function Insights({ items }) {
  if (!items.length) return null;
  const tone = {
    good: "border-[rgba(79,157,118,0.30)] bg-[rgba(79,157,118,0.07)] text-[#7cbb99]",
    warn: "border-[rgba(200,146,47,0.30)] bg-[rgba(200,146,47,0.07)] text-[#d6a955]",
    risk: "border-[rgba(233,53,41,0.32)] bg-[rgba(233,53,41,0.07)] text-[#f04f44]",
    note: "border-[#3a3a3a] bg-[#2c2c2c] text-[#b4b4b4]",
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {items.map((it, i) => (
        <div key={i} className={`rounded-lg border px-4 py-3 ${tone[it.kind]}`}>
          <div className="text-[12.5px] font-medium leading-snug">{it.headline}</div>
          {it.detail && (
            <div className="mt-1 text-[11.5px] opacity-75 leading-relaxed">{it.detail}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Findings worth surfacing, derived only from what is actually held. */
function deriveInsights({ four, m2m, mtm, matrix, expiry, nearby, quality, totalRsf, implied }) {
  const out = [];

  if (four.weUnderwrote && four.nearbySigned) {
    const d = (four.weUnderwrote - four.nearbySigned) / four.nearbySigned;
    if (d > 0.1) out.push({
      kind: "risk",
      headline: `We underwrote ${pct1(d)} above what has signed nearby`,
      detail: `Our ${psf(four.weUnderwrote)} against ${psf(four.nearbySigned)} across ${four.nearbyCount} signed leases in the radius.`,
    });
    else if (d < -0.1) out.push({
      kind: "good",
      headline: `We underwrote ${pct1(Math.abs(d))} below the market`,
      detail: `Nearby leases are signing at ${psf(four.nearbySigned)} against our ${psf(four.weUnderwrote)}.`,
    });
  }

  if (four.brokerSaid && four.rentRollSigned) {
    const d = (four.brokerSaid - four.rentRollSigned) / four.rentRollSigned;
    if (d > 0.15) out.push({
      kind: "warn",
      headline: `The OM's market rent is ${pct1(d)} above the building's own signed rents`,
      detail: `Broker ${psf(four.brokerSaid)} against ${psf(four.rentRollSigned)} in place across ${four.signedCount} leases.`,
    });
  }

  // Mark to market reads as a percentage: how far the rent roll has to travel.
  // A dollar figure is not comparable between a 20,000 SF building and a
  // 400,000 SF one, and the comparison is the whole reason to look at it.
  const upside = m2m.reduce((s, r) => s + (r.annualUpside || 0), 0);
  if (mtm && Number.isFinite(mtm.pct) && Math.abs(mtm.pct) > 0.01) {
    const top = m2m.filter((r) => r.annualUpside > 0).slice(0, 3);
    const share = upside > 0 ? top.reduce((s, r) => s + r.annualUpside, 0) / upside : null;
    out.push({
      kind: mtm.pct > 0 ? "good" : "risk",
      headline: mtm.pct > 0
        ? `${pct1(mtm.pct)} of mark to market`
        : `Rents here are ${pct1(Math.abs(mtm.pct))} ABOVE market`,
      detail: [
        `${psf(mtm.inPlace)} in place against ${psf(mtm.market)} market, from ${mtm.source}.`,
        upside ? `Worth ${money(Math.abs(upside))} a year across ${sf0(mtm.leasedSf)} SF leased.` : null,
        share && top.length ? `${pct1(share)} of it sits in ${top.length} suite${top.length === 1 ? "" : "s"} — ${top.map((t) => t.unit_label || t.tenant).filter(Boolean).join(", ")}.` : null,
      ].filter(Boolean).join(" "),
    });
  }

  const vacant = m2m.filter((r) => r.is_vacant);
  if (vacant.length) {
    const vsf = vacant.reduce((s, r) => s + (Number(r.sf) || 0), 0);
    out.push({
      kind: vsf / (totalRsf || 1) > 0.15 ? "warn" : "note",
      headline: `${vacant.length} vacant suite${vacant.length === 1 ? "" : "s"}, ${sf0(vsf)} SF`,
      detail: totalRsf ? `${pct1(vsf / totalRsf)} of the property.` : null,
    });
  }

  const soon = expiry.slice(0, 2).reduce((s, r) => s + r.sf, 0);
  if (soon && totalRsf && soon / totalRsf > 0.2) {
    out.push({
      kind: "warn",
      headline: `${pct1(soon / totalRsf)} of the building rolls within two years`,
      detail: `${sf0(soon)} SF expiring — the mark to market is capturable sooner than usual, and so is the downside.`,
    });
  }

  const bases = matrix.bases || [];
  if (!bases.length) {
    out.push({
      kind: "note",
      headline: "No operating expenses held",
      detail: "Nothing could be reconciled to a stated total, so nothing was loaded rather than a figure being inferred.",
    });
  } else if (Object.keys(matrix.disagrees || {}).length) {
    const cats = Object.keys(matrix.disagrees);
    out.push({
      kind: "warn",
      headline: `${cats.length} expense line${cats.length === 1 ? "" : "s"} disagree across bases`,
      detail: `${cats.slice(0, 4).join(", ")} differ by more than 15% between ${bases.join(" and ")}.`,
    });
  }

  if (implied && implied.verdict === "insufficient evidence") {
    out.push({
      kind: "risk",
      headline: "Not enough nearby evidence to support a market rent",
      detail: `${implied.n} usable comp${implied.n === 1 ? "" : "s"} in range. Widen the radius, or treat the assumption as unevidenced.`,
    });
  }

  const excluded = quality.filter((q) => q.severity === "excluded").length;
  if (excluded) out.push({
    kind: "note",
    headline: `${excluded} row${excluded === 1 ? "" : "s"} were excluded by the extraction`,
    detail: "Gated rather than loaded. The reasons are in the data-quality panel.",
  });

  return out;
}

/* ── mark to market ───────────────────────────────────────────────── */
function MarkToMarket({ rows, mtm }) {
  const real = rows.filter((r) => r.inPlace || r.market);
  const upside = real.reduce((s, r) => s + (r.annualUpside || 0), 0);
  const maxAbs = Math.max(1, ...real.map((r) => Math.abs(r.annualUpside || 0)));
  // The percentage leads. The annual dollars are the consequence of it and sit
  // underneath, because only the percentage compares between buildings.
  const up = mtm && Number.isFinite(mtm.pct) ? mtm.pct : null;
  return (
    <Card title="Mark to market" sub="per suite, sorted by what the gap is worth"
          right={
            <span className="text-right">
              <span className={`block text-[15px] font-light tabular-nums ${
                up == null ? "text-[#8a8a8a]" : up > 0 ? "text-[#4f9d76]" : "text-[#e93529]"}`}>
                {up == null ? "—" : `${up > 0 ? "+" : ""}${pct1(up)}`}
              </span>
              <span className="block text-[10px] text-[#8a8a8a]">{money(upside)}/yr</span>
            </span>
          }>
      {!real.length ? <Empty>No rent roll is held for this deal.</Empty> : (
        <div className="max-h-[22rem] overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-[#242424] z-10">
              <tr className="text-left border-b border-[#3a3a3a]">
                <th className={TH}>Suite</th><th className={TH}>Tenant</th>
                <th className={`${TH} text-right`}>SF</th>
                <th className={`${TH} text-right`}>In place</th>
                <th className={`${TH} text-right`}>Market</th>
                <th className={`${TH} text-right`}>Gap</th>
                <th className={`${TH} w-28`}>Upside</th>
                <th className={TH}>Expires</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#303030]">
              {real.map((r) => (
                <tr key={r.id} className="hover:bg-[#2c2c2c]">
                  <td className={TD}>{r.unit_label || "—"}</td>
                  <td className={TD}>
                    {r.is_vacant
                      ? <span className="px-1.5 py-0.5 rounded bg-[rgba(200,146,47,0.12)] text-[#d6a955] text-[11px]">Vacant</span>
                      : <span className="truncate block max-w-[13rem]">{r.tenant || "—"}</span>}
                  </td>
                  <td className={`${TD} text-right tabular-nums text-[#8a8a8a]`}>{sf0(r.sf)}</td>
                  <td className={`${TD} text-right tabular-nums`}>{psf(r.inPlace)}</td>
                  <td className={`${TD} text-right tabular-nums`}>{psf(r.market)}</td>
                  <td className={`${TD} text-right tabular-nums ${r.gap > 0 ? "text-[#4f9d76]" : r.gap < 0 ? "text-[#e93529]" : "text-[#8a8a8a]"}`}>
                    {r.gap ? `${r.gap > 0 ? "+" : ""}${r.gap.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-2">
                    {r.annualUpside ? (
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 h-1.5 bg-[#2c2c2c] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${r.annualUpside > 0 ? "bg-[#4f9d76]" : "bg-[#e93529]"}`}
                               style={{ width: `${(Math.abs(r.annualUpside) / maxAbs) * 100}%` }} />
                        </div>
                        <span className="text-[11px] tabular-nums text-[#8a8a8a] w-14 text-right">
                          {money(r.annualUpside)}
                        </span>
                      </div>
                    ) : <span className="text-[11px] text-[#7a7a7a]">—</span>}
                  </td>
                  <td className={`${TD} text-[#8a8a8a]`}>{r.lease_end || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ── OpEx ─────────────────────────────────────────────────────────── */
function OpexMatrix({ matrix, totalRsf }) {
  const { bases, categories, cell, statedTotal, computed, disagrees } = matrix;
  if (!bases.length) {
    return (
      <Card title="Operating expenses">
        <Empty>
          Nothing is held. An expense figure that cannot be reconciled to a
          stated total is one we do not have, and leaving it blank is honest
          where inferring it would not be.
        </Empty>
      </Card>
    );
  }
  const show = (v) => (v ? (totalRsf ? `$${(v / totalRsf).toFixed(2)}` : money(v)) : "—");
  const biggest = Math.max(...categories.map((c) =>
    Math.max(...bases.map((b) => cell[c + "||" + b] || 0))), 1);
  return (
    <Card title={`Operating expenses ${totalRsf ? "($/SF)" : "(annual)"}`}
          sub={`${bases.length} basis${bases.length === 1 ? "" : "es"}, kept separate rather than blended`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left border-b border-[#3a3a3a]">
              <th className={TH}>Category</th>
              <th className={TH}>Share</th>
              {bases.map((b) => <th key={b} className={`${TH} text-right`}>{b}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#303030]">
            {categories.map((c) => {
              const top = Math.max(...bases.map((b) => cell[c + "||" + b] || 0));
              return (
                <tr key={c} className={disagrees[c] ? "bg-[rgba(200,146,47,0.05)]" : ""}>
                  <td className={TD}>
                    {c}
                    {disagrees[c] && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[rgba(200,146,47,0.12)] text-[#d6a955]">
                        differ
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 w-32">
                    <div className="h-1.5 bg-[#2c2c2c] rounded-full overflow-hidden">
                      <div className="h-full bg-[rgba(233,53,41,0.55)] rounded-full"
                           style={{ width: `${(top / biggest) * 100}%` }} />
                    </div>
                  </td>
                  {bases.map((b) => (
                    <td key={b} className={`${TD} text-right tabular-nums`}>{show(cell[c + "||" + b])}</td>
                  ))}
                </tr>
              );
            })}
            <tr className="border-t-2 border-[#3a3a3a]">
              <td className="px-4 py-2.5 text-[12.5px] font-semibold text-[#f4f4f4]">Total</td>
              <td />
              {bases.map((b) => {
                const stated = statedTotal[b], comp = computed[b];
                const ties = stated && comp && Math.abs(stated - comp) / stated < 0.005;
                return (
                  <td key={b} className="px-4 py-2.5 text-right">
                    <div className="text-[13px] font-semibold tabular-nums text-[#f4f4f4]">{show(comp)}</div>
                    {stated ? (
                      <div className={`text-[10px] ${ties ? "text-[#7cbb99]" : "text-[#d6a955]"}`}>
                        {ties ? "ties to its stated total" : `stated ${show(stated)}`}
                      </div>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── expiry ───────────────────────────────────────────────────────── */
function ExpirySchedule({ rows, totalRsf }) {
  const max = Math.max(1, ...rows.map((r) => r.sf));
  const any = rows.some((r) => r.sf > 0);
  return (
    <Card title="Lease expiry" sub="when the mark to market can actually be captured">
      {!any ? <Empty>No lease end dates are held.</Empty> : (
        <div className="px-5 py-4 space-y-2">
          {rows.map((r) => (
            <div key={r.year} className="flex items-center gap-3">
              <span className="w-9 text-[11px] tabular-nums text-[#8a8a8a]">{r.year}</span>
              <div className="flex-1 h-5 bg-[#2c2c2c] rounded-md overflow-hidden relative">
                <div className="h-full bg-gradient-to-r from-[rgba(233,53,41,0.45)] to-[rgba(233,53,41,0.18)]"
                     style={{ width: `${(r.sf / max) * 100}%` }} />
                {r.tenants.length > 0 && (
                  <span className="absolute inset-y-0 left-2 flex items-center text-[10px] text-[#8a8a8a] truncate max-w-[70%]">
                    {r.tenants.slice(0, 2).join(", ")}{r.tenants.length > 2 ? ` +${r.tenants.length - 2}` : ""}
                  </span>
                )}
              </div>
              <span className="w-20 text-right text-[11px] tabular-nums text-[#8a8a8a]">{r.sf ? sf0(r.sf) : ""}</span>
              <span className="w-12 text-right text-[11px] tabular-nums text-[#8a8a8a]">
                {r.sf && totalRsf ? pct1(r.sf / totalRsf) : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ── comp evidence: three tiers, deliberately unlike each other ───── */
function CompEvidence({ used, suggested, picked, onPick, onUnpick, implied, radius, setRadius }) {
  const [tab, setTab] = useState("suggested");
  const tabs = [
    { k: "used", label: `Used in UW (${used.length})` },
    { k: "suggested", label: `Suggested (${suggested.length})` },
    { k: "picked", label: `Hand-picked (${picked.length})` },
  ];
  return (
    <Card
      title="Comparable evidence"
      sub="what the underwriting used, what the engine suggests, what somebody chose"
      right={
        <div className="flex items-center gap-1">
          {[1, 2, 3, 5].map((r) => (
            <button key={r} onClick={() => setRadius(r)}
              className={`px-2 py-1 rounded-lg text-[11px] transition ${radius === r
                ? "bg-[rgba(233,53,41,0.10)] text-[#f04f44] ring-1 ring-[rgba(233,53,41,0.32)]"
                : "text-[#8a8a8a] hover:text-[#b4b4b4]"}`}>{r} mi</button>
          ))}
        </div>
      }>
      {implied && (
        <div className="px-5 py-3 border-b border-[#3a3a3a] flex items-baseline gap-3 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-[#8a8a8a]">Implied market rent</span>
          {implied.rent ? (
            <>
              <span className="text-[20px] font-semibold tabular-nums text-[#f4f4f4]">{psf(implied.rent)}</span>
              <span className="text-[11px] text-[#8a8a8a]">
                from {implied.n} comps, {psf(implied.low)}–{psf(implied.high)} ·{" "}
                <span className={implied.verdict === "well supported" ? "text-[#4f9d76]"
                  : implied.verdict === "supported" ? "text-[#f04f44]" : "text-[#c8922f]"}>
                  {implied.verdict}
                </span>
              </span>
            </>
          ) : (
            <span className="text-[12.5px] text-[#e93529]">
              insufficient evidence — {implied.n} usable comp{implied.n === 1 ? "" : "s"} in range
            </span>
          )}
        </div>
      )}
      <div className="px-5 pt-3 flex gap-1">
        {tabs.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 rounded-lg text-[12px] transition ${tab === t.k
              ? "bg-[#2c2c2c] text-[#f4f4f4]" : "text-[#8a8a8a] hover:text-[#b4b4b4]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-h-72 overflow-auto mt-2">
        {tab === "used" && (used.length ? (
          <table className="w-full">
            <thead><tr className="text-left border-b border-[#3a3a3a]">
              <th className={TH}>Address</th><th className={TH}>Tenant</th>
              <th className={`${TH} text-right`}>SF</th><th className={`${TH} text-right`}>Rent</th>
              <th className={TH}>From</th>
            </tr></thead>
            <tbody className="divide-y divide-[#303030]">
              {used.map((c) => (
                <tr key={c.id} className="hover:bg-[#2c2c2c]">
                  <td className={TD}>{c.address || "—"}</td>
                  <td className={`${TD} text-[#8a8a8a]`}>{c.tenant || "—"}</td>
                  <td className={`${TD} text-right tabular-nums text-[#8a8a8a]`}>{sf0(c.suite_sf)}</td>
                  <td className={`${TD} text-right tabular-nums`}>{psf(c.lease_rate)}</td>
                  <td className="px-4 py-2 text-[10px] text-[#7a7a7a] truncate max-w-[12rem]">
                    {c.source_file || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty>No comps came through this deal's own source files.</Empty>)}

        {tab === "suggested" && (suggested.length ? (
          <div className="divide-y divide-[#303030]">
            {suggested.map((s) => (
              <div key={s.comp.id} className="px-5 py-3 flex items-start gap-3 hover:bg-[#2c2c2c]">
                <div className="w-10 shrink-0 text-center">
                  <div className={`text-[15px] font-semibold tabular-nums ${s.score >= 70 ? "text-[#4f9d76]"
                    : s.score >= 50 ? "text-[#f04f44]" : "text-[#8a8a8a]"}`}>{s.score}</div>
                  <div className="text-[9px] uppercase tracking-wider text-[#7a7a7a]">score</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[12.5px] text-[#f4f4f4]">{s.comp.address || "—"}</span>
                    <span className="text-[12.5px] tabular-nums text-[#f4f4f4] font-medium">{psf(s.comp.lease_rate)}</span>
                    <span className="text-[11px] text-[#8a8a8a]">{sf0(s.comp.suite_sf)} SF</span>
                  </div>
                  <div className="text-[11px] text-[#8a8a8a] mt-0.5">{s.reasons.join(" · ")}</div>
                </div>
                <button onClick={() => onPick(s.comp)}
                  className="text-[11px] px-2 py-1 rounded-lg text-[#f04f44] hover:bg-[rgba(233,53,41,0.10)] shrink-0">
                  promote
                </button>
              </div>
            ))}
          </div>
        ) : <Empty>Nothing in the library scores well enough against this subject.</Empty>)}

        {tab === "picked" && (picked.length ? (
          <div className="divide-y divide-[#303030]">
            {picked.map((p) => {
              const c = p.comps || {};
              return (
                <div key={p.comp_id} className="px-5 py-2.5 flex items-center gap-3 hover:bg-[#2c2c2c]">
                  <span className="px-1.5 py-0.5 rounded bg-[rgba(200,146,47,0.12)] text-[#d6a955] text-[10px] shrink-0">
                    judgement
                  </span>
                  <span className="text-[12.5px] text-[#f4f4f4] flex-1 truncate">{c.address || p.comp_id}</span>
                  <span className="text-[12.5px] tabular-nums text-[#b4b4b4]">{psf(c.lease_rate)}</span>
                  <button onClick={() => onUnpick(p.comp_id)}
                    className="text-[11px] text-[#8a8a8a] hover:text-[#e93529]">remove</button>
                </div>
              );
            })}
          </div>
        ) : (
          <Empty>
            Nothing hand-picked. Promoting a suggestion stores it here, and a
            reload will not remove it — that is what separates judgement from
            sourced evidence.
          </Empty>
        ))}
      </div>
    </Card>
  );
}

/* ── data quality ─────────────────────────────────────────────────── */
function DataQuality({ rows }) {
  const order = ["excluded", "review", "warn"];
  const tone = { excluded: "bg-[rgba(233,53,41,0.12)] text-[#f04f44]", review: "bg-[rgba(200,146,47,0.12)] text-[#d6a955]", warn: "bg-[#2c2c2c] text-[#b4b4b4]" };
  const shown = rows.filter((r) => order.includes(r.severity));
  const counts = order.map((s) => [s, rows.filter((r) => r.severity === s).length]).filter(([, n]) => n);
  return (
    <Card title="Data quality" sub="what the extraction gated, flagged or excluded"
          right={<div className="flex gap-1.5">
            {counts.map(([s, n]) => (
              <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] ${tone[s]}`}>{n} {s}</span>
            ))}
          </div>}>
      {!shown.length ? <Empty>Nothing was gated or flagged on this deal.</Empty> : (
        <div className="max-h-64 overflow-y-auto divide-y divide-[#303030]">
          {shown.slice(0, 60).map((r) => (
            <div key={r.id} className="px-5 py-2.5">
              <div className="flex items-start gap-2.5">
                <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider shrink-0 ${tone[r.severity]}`}>
                  {r.severity}
                </span>
                <div className="min-w-0">
                  <div className="text-[12px] text-[#b4b4b4]">{r.category}</div>
                  <div className="text-[11px] text-[#8a8a8a] leading-relaxed">{r.detail}</div>
                  {r.source_file && (
                    <div className="text-[10px] text-[#7a7a7a] mt-0.5 truncate">
                      {r.source_file}{r.source_sheet ? ` · ${r.source_sheet}` : ""}
                      {r.source_row ? ` · row ${r.source_row}` : ""}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ── page ─────────────────────────────────────────────────────────── */
export default function DealDashboard({ deal }) {
  const dealId = deal?.id;
  const totalRsf = Number(deal?.square_footage) || null;

  const [rentRoll, setRentRoll] = useState([]);
  const [marketRent, setMarketRent] = useState([]);
  const [opex, setOpex] = useState([]);
  const [quality, setQuality] = useState([]);
  const [picked, setPicked] = useState([]);
  const [used, setUsed] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [opexNearby, setOpexNearby] = useState([]);
  const [radius, setRadius] = useState(2);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    let live = true;
    setReady(false);
    Promise.all([
      loadRentRoll(dealId), loadMarketRent(dealId), loadOpexLines(dealId),
      loadDataQuality(dealId), loadHandpickedComps(dealId), loadAssignedComps(dealId),
    ]).then(([rr, mr, ox, dq, hp, ac]) => {
      if (!live) return;
      setRentRoll(rr); setMarketRent(mr); setOpex(ox); setQuality(dq);
      setPicked(hp); setUsed((ac || []).map((a) => a.comps || a).filter(Boolean));
      setReady(true);
    });
    return () => { live = false; };
  }, [dealId]);

  useEffect(() => {
    const lat = Number(deal?.latitude), lng = Number(deal?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { setNearby([]); return; }
    let live = true;
    loadNearbyComps(lat, lng, radius, { type: "lease" })
      .then((c) => { if (live) setNearby(c); });
    return () => { live = false; };
  }, [deal?.latitude, deal?.longitude, radius]);

  // Expense evidence is scarcer than rent evidence and travels with sales as
  // well as leases, so it is gathered on its own, wider, fixed radius rather
  // than riding the comp-set toggle.
  useEffect(() => {
    const lat = Number(deal?.latitude), lng = Number(deal?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { setOpexNearby([]); return; }
    let live = true;
    loadNearbyComps(lat, lng, OPEX_RADIUS_MILES, { type: null, limit: 800 })
      .then((c) => { if (live) setOpexNearby((c || []).filter((x) => Number(x.opex_psf) > 0)); });
    return () => { live = false; };
  }, [deal?.latitude, deal?.longitude]);

  const four = useMemo(() => buildFourWayRent({ rentRoll, marketRent, nearbyComps: nearby }),
    [rentRoll, marketRent, nearby]);
  const m2m = useMemo(() => buildMarkToMarket(rentRoll, marketRent), [rentRoll, marketRent]);
  const matrix = useMemo(() => buildOpexMatrix(opex, totalRsf), [opex, totalRsf]);
  const expiry = useMemo(() => buildExpirySchedule(rentRoll), [rentRoll]);

  const suggested = useMemo(() => suggestComps(nearby, {
    lat: Number(deal?.latitude), lng: Number(deal?.longitude),
    sf: totalRsf, clearHeight: deal?.clear_height, doorType: deal?.door_type,
  }, { radiusMiles: radius, excludeIds: used.map((u) => u.id) }),
    [nearby, deal, totalRsf, radius, used]);

  const implied = useMemo(() => impliedMarketRent(suggested), [suggested]);
  const mtm = useMemo(() => markToMarket(rentRoll, marketRent, {
    impliedMarket: implied?.rent || null,
  }), [rentRoll, marketRent, implied]);
  const insights = useMemo(() => deriveInsights({
    four, m2m, mtm, matrix, expiry, nearby, quality, totalRsf, implied,
  }), [four, m2m, mtm, matrix, expiry, nearby, quality, totalRsf, implied]);

  async function pick(comp) {
    if (picked.some((p) => p.comp_id === comp.id)) return;
    await addHandpickedComp(dealId, comp.id);
    setPicked([...picked, { comp_id: comp.id, deal_id: dealId, comps: comp }]);
  }
  async function unpick(compId) {
    await removeHandpickedComp(dealId, compId);
    setPicked(picked.filter((p) => p.comp_id !== compId));
  }

  if (!dealId) return null;
  if (!ready) return <div className="p-8 text-[12.5px] text-[#8a8a8a]">Loading the deal's evidence…</div>;

  if (!rentRoll.length && !marketRent.length && !opex.length) {
    return (
      <div className="p-5">
        <Card title="No comp-database record">
          <Empty>
            This deal is not linked to the comp database, so there is no rent
            roll, market rent or expense detail to show. Deals link by
            <code className="mx-1 text-[#8a8a8a]">comp_db_deal_id</code>; if this
            one should carry data, it needs mapping and reloading.
          </Empty>
        </Card>
      </div>
    );
  }

  const occupied = rentRoll.filter((r) => !r.is_vacant);
  const occSf = occupied.reduce((s, r) => s + (Number(r.sf) || 0), 0);
  const upside = m2m.reduce((s, r) => s + (r.annualUpside || 0), 0);
  const opexPsf = matrix.bases.length && totalRsf
    ? (matrix.computed[matrix.bases[0]] || 0) / totalRsf : null;

  return (
    <div className="p-5 space-y-4">
      {/* KPI strip */}
      <div className={`${CARD} flex flex-wrap divide-x divide-[#303030]`}>
        <Kpi label="In place" value={psf(four.rentRollSigned)}
             sub={`${occupied.length} signed lease${occupied.length === 1 ? "" : "s"}, weighted by area`} />
        <Kpi label="Market" value={psf(four.weUnderwrote || four.brokerSaid)}
             sub={four.weUnderwrote ? "our underwriting" : "the broker's figure"} tone="sky" />
        <Kpi label="Mark to market"
             value={Number.isFinite(mtm.pct) ? `${mtm.pct > 0 ? "+" : ""}${pct1(mtm.pct)}` : "—"}
             sub={Number.isFinite(mtm.pct)
               ? `${money(Math.abs(upside))}/yr · ${psf(mtm.inPlace)} to ${psf(mtm.market)}`
               : "no market rent is held to compare against"}
             tone={!Number.isFinite(mtm.pct) ? "slate" : mtm.pct > 0 ? "emerald" : "rose"}
             hint={mtm.how} />
        <Kpi label="Occupancy" value={totalRsf ? pct1(occSf / totalRsf) : "—"}
             sub={totalRsf ? `${sf0(occSf)} of ${sf0(totalRsf)} SF` : "no RSF held"}
             tone={totalRsf && occSf / totalRsf < 0.85 ? "amber" : "slate"} />
        <Kpi label="OpEx" value={opexPsf ? psf(opexPsf) : "not held"}
             sub={matrix.bases.length ? matrix.bases[0] : "nothing reconciled"} />
        <Kpi label="Evidence" value={String(nearby.length)}
             sub={`signed comps within ${radius} mi`}
             tone={nearby.length < 3 ? "rose" : nearby.length < 8 ? "amber" : "emerald"} />
      </div>

      <Insights items={insights} />

      {/* The headline panel carries the icon shape, per the brandbook's use of
          it as "a dynamic element that frames, contains, highlights or
          organizes the items in a composition". One panel, not every panel -
          used everywhere it would stop meaning anything. */}
      <Card title="Four-way rent comparison"
            sub="what the broker said, where we underwrote, what is signed here, what is signed nearby">
        <GableHeadline tone="red">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#303030]">
          {[
            ["Broker said", four.brokerSaid, "from the OM"],
            ["We underwrote", four.weUnderwrote, "our MLA"],
            ["Signed here", four.rentRollSigned, `${four.signedCount} leases, by area`],
            ["Signed nearby", four.nearbySigned, `${four.nearbyCount} comps in ${radius} mi`],
          ].map(([k, v, note]) => (
            <div key={k} className="px-5 py-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8a8a8a]">{k}</div>
              <div className={`mt-1.5 text-[24px] leading-none font-light tracking-tight tabular-nums ${v ? "text-[#f4f4f4]" : "text-[#7a7a7a]"}`}>
                {v ? psf(v) : "not held"}
              </div>
              <div className="mt-1.5 text-[11px] text-[#8a8a8a]">{note}</div>
            </div>
          ))}
        </div>
        </GableHeadline>
      </Card>

      <DealCharts
        deal={deal} rentRoll={rentRoll} marketRent={marketRent} opex={opex}
        nearby={nearby} opexNearby={opexNearby} totalRsf={totalRsf}
        radius={radius} opexRadius={OPEX_RADIUS_MILES} subjectOpexPsf={opexPsf} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <MarkToMarket rows={m2m} mtm={mtm} />
        <OpexMatrix matrix={matrix} totalRsf={totalRsf} />
        <CompEvidence used={used} suggested={suggested} picked={picked}
                      onPick={pick} onUnpick={unpick} implied={implied}
                      radius={radius} setRadius={setRadius} />
        <ExpirySchedule rows={expiry} totalRsf={totalRsf} />
      </div>

      <DataQuality rows={quality} />
    </div>
  );
}
